import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import { Text } from '~/components/core/TextComponents';
import SwitchOrDelete from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/OptionsCard/SwitchOrDelete';
import TextFieldFormik from '~/components/TextFieldFormik';

/**
 * The `options` list of a select or multiselect global field.
 *
 * The FNOL screen's `OptionsCard` is not reused: it reads its option-level category list out of
 * `configuration.categories` on the per-configuration blob and addresses its fields through that
 * screen's Formik paths, so wiring it here would mean reshaping this form into the blob's shape or
 * editing a shared component for a new caller. Option-level category limits are deliberately out
 * of scope for a global field — the field-level limit is the one a global definition can honour
 * identically on every form.
 */
interface OptionRow {
  /** `id`, not `key`: this is the option's shape inside the stored definition, which the backend
   *  validates as a `ConfiguredFieldSelectOption` and which stored claim values hold. */
  id: string;
  desc: string;
  active: boolean;
  isNew?: boolean;
}

interface OptionsFormValues {
  options: OptionRow[];
}

const GlobalFieldOptionsEditor: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<OptionsFormValues>();

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        Options
      </Text>
      <FieldArray name="options">
        {({ push, remove }) => (
          <div className="flex flex-col gap-8">
            {values.options.map((option, index) => (
              <div
                // Keyed by position: an option has no stable id until it is saved, and the two
                // text fields are controlled by Formik paths that are themselves positional.
                key={`option-${index}`}
                className="grid grid-cols-[2fr_2fr_auto] items-center gap-8"
              >
                <TextFieldFormik
                  id={`options.${index}.desc`}
                  label="Display Name"
                  fullWidth
                  // @ts-expect-error TextFieldFormik is an untyped JSX module, so its `onChange`
                  // prop is inferred as the `undefined` default it destructures.
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue(`options.${index}.desc`, event.target.value);
                    // An option id is only ever derived, never typed, and only for a row added in
                    // this dialog: a saved option's id is what claims store as the field's value,
                    // so renaming the option must not move it.
                    if (option.isNew) {
                      setFieldValue(`options.${index}.id`, snakeCase(event.target.value));
                    }
                  }}
                />
                <TextFieldFormik id={`options.${index}.id`} label="Key" fullWidth disabled />
                {/* As on the FNOL screen: only a row added in this dialog can be removed. Claims may
                    already store a saved option's key, so a saved option can only be deactivated. */}
                <SwitchOrDelete
                  id={`options.${index}.active`}
                  isExistingValue={!option.isNew}
                  onDelete={() => remove(index)}
                  ariaLabel={`Remove option ${option.desc || index + 1}`}
                />
              </div>
            ))}
            <div>
              <Button onClick={() => push({ id: '', desc: '', active: true, isNew: true })}>+ Add Option</Button>
            </div>
          </div>
        )}
      </FieldArray>
    </div>
  );
};

export default GlobalFieldOptionsEditor;
